/**
 * Store del token JWT: in-memory (primario) + localStorage (persistenza tra reload).
 *
 * Il token è conservato in una variabile di modulo (in-memory) per evitare accessi
 * ripetuti al localStorage nelle operazioni frequenti. Al caricamento iniziale
 * viene ripristinato dal localStorage se presente.
 *
 * Accanto all'id_token conserva il refresh_token OIDC (se l'IdP lo emette): serve a
 * rinnovare la sessione prima della scadenza dell'id_token (vedi oidc.ts refreshSession)
 * invece di rimandare l'utente al login ogni `exp` (1h con oidc-mock).
 *
 * I cambi di token sono notificati ai sottoscrittori (AuthContext) e sincronizzati tra
 * tab via evento `storage`: un refresh o un logout in un tab si riflette negli altri.
 *
 * NOTA SICUREZZA: localStorage è accessibile da JS sulla stessa origin, quindi
 * esposto a XSS. In un deployment production considerare cookie HttpOnly come
 * alternativa. Per questa slice usiamo localStorage per semplicità e per non
 * bloccare il portale su infrastruttura cookie.
 */

const STORAGE_KEY = 'tenaciousc_jwt';
const REFRESH_STORAGE_KEY = 'tenaciousc_oidc_refresh';
/** Come è stato ottenuto il token (login Entra / admin locale): `entra` è rinnovabile, `break-glass` no. */
const AUTH_MODE_STORAGE_KEY = 'tenaciousc_auth_mode';

export type AuthMode = 'entra' | 'break-glass';

// Token in-memory: inizializzati dal localStorage al primo import del modulo.
let _token: string | null = null;
let _refreshToken: string | null = null;

type TokenListener = (token: string | null) => void;
const _listeners = new Set<TokenListener>();

function _notify(): void {
  for (const listener of _listeners) listener(_token);
}

/** Ripristina i token dal localStorage (chiamato una volta all'init). */
function _init(): void {
  try {
    _token = localStorage.getItem(STORAGE_KEY);
    _refreshToken = localStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    // localStorage non disponibile (es. SSR, test senza jsdom configurato)
    _token = null;
    _refreshToken = null;
  }
  // Sincronizzazione cross-tab: l'evento `storage` scatta solo negli ALTRI tab.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEY) {
        _token = e.newValue;
        _notify();
      } else if (e.key === null) {
        // localStorage.clear() in un altro tab (logout con pulizia completa).
        _token = null;
        _refreshToken = null;
        _notify();
      } else if (e.key === REFRESH_STORAGE_KEY) {
        _refreshToken = e.newValue;
      }
    });
  }
}

_init();

/** Salva il token in-memory e nel localStorage. */
export function setToken(token: string): void {
  _token = token;
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // localStorage non disponibile: il token rimane solo in-memory
  }
  _notify();
}

/** Rimuove token e refresh token (logout). */
export function clearToken(): void {
  _token = null;
  _refreshToken = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(REFRESH_STORAGE_KEY);
    localStorage.removeItem(AUTH_MODE_STORAGE_KEY);
  } catch {
    // ignore
  }
  _notify();
}

/** Restituisce il token corrente o null se non autenticato. */
export function getToken(): string | null {
  return _token;
}

/** Salva (o rimuove, con null) il refresh_token OIDC. */
export function setRefreshToken(refreshToken: string | null): void {
  _refreshToken = refreshToken;
  try {
    if (refreshToken) localStorage.setItem(REFRESH_STORAGE_KEY, refreshToken);
    else localStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch {
    // localStorage non disponibile: il refresh token rimane solo in-memory
  }
}

/** Restituisce il refresh_token OIDC corrente o null (login manuale / IdP senza refresh). */
export function getRefreshToken(): string | null {
  return _refreshToken;
}

/** Registra la modalità di login del token corrente (null la rimuove). */
export function setAuthMode(mode: AuthMode | null): void {
  try {
    if (mode) localStorage.setItem(AUTH_MODE_STORAGE_KEY, mode);
    else localStorage.removeItem(AUTH_MODE_STORAGE_KEY);
  } catch {
    // localStorage non disponibile: la modalità non sopravvive al reload
  }
}

/** Modalità di login del token corrente (letta dallo storage: condivisa tra tab). */
export function getAuthMode(): AuthMode | null {
  try {
    const v = localStorage.getItem(AUTH_MODE_STORAGE_KEY);
    return v === 'entra' || v === 'break-glass' ? v : null;
  } catch {
    return null;
  }
}

/** Sottoscrive i cambi di token (set/clear/sync cross-tab). Restituisce l'unsubscribe. */
export function subscribeToken(listener: TokenListener): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

/** Forza il valore del token in-memory (usato nei test per isolare lo store). */
export function _setTokenForTest(token: string | null): void {
  _token = token;
}

/** Forza il valore del refresh token in-memory (usato nei test per isolare lo store). */
export function _setRefreshTokenForTest(refreshToken: string | null): void {
  _refreshToken = refreshToken;
}
